import { useEffect, useMemo, useState } from 'react';
// (1) WHITELIST icone lucide: SOLO i ~21 nomi usati dal seed della Guida
// (backend/apps/admin_panel/migrations/0017_guida_seed.py) + fallback.
// NIENTE `import *`: il bundle resta leggero. Il campo `icona` e' una STRINGA
// LIBERA (DEC-046): un nome fuori whitelist NON deve rompere la pagina -> BookOpen.
import {
  Search, ChevronDown, BookOpen,
  Smartphone, Rocket, Home, BarChart3, HardHat, Calendar, Folder,
  Users, ClipboardList, Tag, Receipt, Truck, Sparkles, Activity,
  Newspaper, Lightbulb, Plug, Gift, LifeBuoy, Settings, HelpCircle,
} from 'lucide-react';
import { guida as guidaApi } from '../utils/api';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';

const ICONE = {
  Smartphone, Rocket, Home, BarChart3, HardHat, Calendar, Folder,
  Users, ClipboardList, Tag, Receipt, Truck, Sparkles, Activity,
  Newspaper, Lightbulb, Plug, Gift, LifeBuoy, Settings, HelpCircle,
};

function IconaSezione({ nome, ...props }) {
  const Comp = (nome && ICONE[nome]) || BookOpen; // fallback: nome sconosciuto
  return <Comp {...props} />;
}

// Chiave stabile per lo stato aperto/chiuso di una scheda (il payload pubblico
// non espone id: uso chiave-sezione + ordine + indice).
const keyScheda = (chiaveSez, sc, i) => `${chiaveSez}::${sc.ordine}::${i}`;

// Colore per ZONA (dal design system, come la sidebar): la testata di sezione
// prende il colore della sua zona → i titoli si distinguono a colpo d'occhio
// dalle schede (bianche) e ogni zona è riconoscibile dal colore.
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;
const ZONA_COLORE = {
  'Per iniziare': 'var(--zone-violet)',
  'Panoramica': 'var(--zone-blue)',
  'Operativita': 'var(--zone-green)',
  'Clienti e vendite': 'var(--zone-amber)',
  'Acquisti': 'var(--zone-acqua)',
  'mAIstro AI': 'var(--zone-magenta)',
  'Servizi': 'var(--zone-slate)',
  'Assistenza': 'var(--brand)',
};
const coloreZona = (gruppo) => ZONA_COLORE[gruppo] || 'var(--brand)';

export default function Guida() {
  const [sezioni, setSezioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [aperte, setAperte] = useState({});

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError('');
    guidaApi.struttura()
      .then((r) => { if (vivo) setSezioni(r.data?.sezioni ?? []); })
      .catch(() => { if (vivo) setError('Non riusciamo a caricare la guida in questo momento. Riprova tra poco.'); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  const q = search.trim().toLowerCase();

  // Ricerca client-side su titolo sezione + titolo/corpo scheda. Le attive sono
  // GIA' filtrate dall'API (views_guida.costruisci_struttura): qui non si rifiltra.
  const sezioniFiltrate = useMemo(() => {
    if (!q) return sezioni.map((s) => ({ ...s, _schede: s.schede ?? [] }));
    const out = [];
    for (const s of sezioni) {
      const schede = s.schede ?? [];
      const sezMatch = (s.titolo || '').toLowerCase().includes(q);
      const schedeMatch = schede.filter(
        (sc) => (sc.titolo || '').toLowerCase().includes(q) || (sc.corpo || '').toLowerCase().includes(q),
      );
      if (sezMatch || schedeMatch.length) {
        out.push({ ...s, _schede: sezMatch ? schede : schedeMatch });
      }
    }
    return out;
  }, [sezioni, q]);

  // (2) Ordine delle ZONE derivato dai DATI: le sezioni arrivano gia' ordinate
  // per `ordine`; l'ordine di ciascun gruppo = primo `ordine` visto. Niente
  // elenco hardcoded da mantenere allineato al pannello AIstotele.
  const zone = useMemo(() => {
    const map = new Map();
    for (const s of sezioniFiltrate) {
      const g = s.gruppo || 'Altro';
      if (!map.has(g)) map.set(g, { gruppo: g, ordineMin: s.ordine ?? 9999, sezioni: [] });
      map.get(g).sezioni.push(s);
    }
    return [...map.values()].sort((a, b) => a.ordineMin - b.ordineMin);
  }, [sezioniFiltrate]);

  const toggle = (key) => setAperte((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div style={{ padding: '28px 20px 40px 32px', width: '100%', boxSizing: 'border-box' }}>
      {/* Intestazione */}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--ink)' }}>Guida all'uso</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>
          Come si usa mAIstro, spiegato dentro mAIstro. Cerca o sfoglia per sezione.
        </p>
      </div>

      {/* Ricerca */}
      <div style={{ position: 'relative', maxWidth: 460, marginBottom: 26 }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', display: 'flex' }}>
          <Search size={16} strokeWidth={2} />
        </span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca nella guida…"
          aria-label="Cerca nella guida"
          style={{ width: '100%', minHeight: 44, padding: '10px 12px 10px 36px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' }}
        />
      </div>

      {/* Stato: LOADING (skeleton) */}
      {loading && (
        <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)', boxShadow: CARD_SHADOW, padding: 18 }}>
              <div style={{ width: '38%', height: 16, borderRadius: 6, background: 'var(--chip)', marginBottom: 14 }} />
              <div style={{ width: '100%', height: 40, borderRadius: 8, background: 'var(--chip)', marginBottom: 8 }} />
              <div style={{ width: '100%', height: 40, borderRadius: 8, background: 'var(--chip)' }} />
            </div>
          ))}
        </div>
      )}

      {/* Stato: ERROR */}
      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '56px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--ds-muted)' }}><BookOpen size={40} strokeWidth={1.5} /></div>
          <div style={{ fontSize: 15, color: 'var(--ink)' }}>{error}</div>
        </div>
      )}

      {/* Stato: EMPTY guida (nessuna sezione attiva) */}
      {!loading && !error && sezioni.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: 'var(--ds-muted)' }}><BookOpen size={44} strokeWidth={1.5} /></div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>La guida è in preparazione</div>
          <div style={{ fontSize: 14, color: 'var(--ds-muted)' }}>Presto troverai qui le istruzioni per usare mAIstro.</div>
        </div>
      )}

      {/* Stato: EMPTY ricerca (guida piena ma nessun risultato) */}
      {!loading && !error && sezioni.length > 0 && zone.length === 0 && (
        <div style={{ textAlign: 'center', padding: '56px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--ds-muted)' }}><Search size={40} strokeWidth={1.5} /></div>
          <div style={{ fontSize: 15, color: 'var(--ink)' }}>Nessun risultato per «{search.trim()}»</div>
          <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 6 }}>Prova con un'altra parola.</div>
        </div>
      )}

      {/* Contenuto: zone → sezioni → schede (accordion) */}
      {!loading && !error && zone.map((z) => {
        const col = coloreZona(z.gruppo);
        return (
        <section key={z.gruppo} style={{ marginBottom: 30 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: col }}>{z.gruppo}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {z.sezioni.map((s) => (
              <div key={s.chiave} style={{ background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderLeft: `4px solid ${col}`, borderRadius: 'var(--radius-card)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
                {/* Testata sezione — fascia colorata per zona: si distingue a colpo d'occhio dalle schede (bianche) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: tint(col, 12), borderBottom: (s._schede.length ? '1px solid var(--ds-border)' : 'none') }}>
                  <span style={{ display: 'flex', color: col }}><IconaSezione nome={s.icona} size={18} strokeWidth={2} /></span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{s.titolo}</span>
                  {s._schede.length > 0 && (
                    <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 500, color: col }}>{s._schede.length}</span>
                  )}
                </div>

                {/* EMPTY per-sezione: sezione attiva senza schede → non sembra rotta */}
                {s._schede.length === 0 ? (
                  <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--ds-muted)', fontStyle: 'italic' }}>
                    Contenuti in arrivo per questa sezione.
                  </div>
                ) : (
                  s._schede.map((sc, i) => {
                    const key = keyScheda(s.chiave, sc, i);
                    const open = q ? true : !!aperte[key]; // in ricerca: aperte per mostrare il match
                    return (
                      <div key={key} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--ds-border)' }}>
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          aria-expanded={open}
                          style={{ width: '100%', minHeight: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                        >
                          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', flex: 1 }}>{sc.titolo}</span>
                          <ChevronDown size={18} strokeWidth={2} style={{ color: 'var(--ds-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                        </button>
                        {open && (
                          <div style={{ padding: '0 16px 14px', fontSize: 14, lineHeight: 1.6, color: 'var(--ds-text)', whiteSpace: 'pre-wrap' }}>
                            {sc.corpo}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        </section>
        );
      })}
    </div>
  );
}
